import { Box, Button, Card, Group, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { IconFileText, IconFileDownload } from "@tabler/icons-react";
import type { ChargeContract } from "../../../../apis/chargeContract";
import { ContractStatusBadge } from "../../../../components/billing/ContractStatusBadge";
import { formatDate } from "../../../../utils/format";

const revenueModelLabel: Record<string, string> = {
  SUBSCRIPTION: "Захиалга",
  TRANSACTION : "Гүйлгээ",
  HYBRID      : "Холимог",
  CUSTOM      : "Тусгай"
};

interface Props {
  contract: ChargeContract;
  /** Open the contract PDF in a new tab. When omitted the button is disabled. */
  onViewPdf?: () => void;
  /** Shows the loading spinner while the PDF blob is being fetched. */
  pdfLoading?: boolean;
}

const heroWash = {
  background:
    "linear-gradient(135deg, var(--mantine-color-brand-6), var(--mantine-color-brand-8))"
};

// Infer the revenue model from the present component types when the contract
// does not carry an explicit model field.
function inferRevenueModel(contract: ChargeContract): string {
  const types = new Set((contract.components ?? []).map((c) => c.componentType));
  const hasSub = types.has("RECURRING_SUBSCRIPTION");
  const hasTx = types.has("TRANSACTION_FEE");
  if (hasSub && hasTx) return "HYBRID";
  if (hasTx) return "TRANSACTION";
  if (hasSub) return "SUBSCRIPTION";
  return "CUSTOM";
}

export function ContractHero({ contract, onViewPdf, pdfLoading }: Props) {
  const model = inferRevenueModel(contract);
  const pdfAvailable = Boolean(onViewPdf);

  return (
    <Card radius="md" padding="lg" style={heroWash}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <ThemeIcon variant="white" color="brand" size={48} radius="md">
            <IconFileText size={24} />
          </ThemeIcon>
          <Box>
            <Text size="xs" fw={700} c="white" tt="uppercase" style={{ letterSpacing: "0.08em", opacity: 0.85 }}>
              Гэрээ
            </Text>
            <Title order={3} c="white" style={{ fontVariantNumeric: "tabular-nums" }}>
              {contract.contractNumber}
            </Title>
            <Text size="sm" c="white" mt={4} style={{ opacity: 0.9 }}>
              Хүчинтэй: {formatDate(contract.effectiveFrom)} →{" "}
              {contract.effectiveUntil ? formatDate(contract.effectiveUntil) : "одоог хүртэл"}
            </Text>
          </Box>
        </Group>

        <Box ta="right">
          <ContractStatusBadge status={contract.status} />
          <Text size="sm" c="white" mt="xs" style={{ opacity: 0.9 }}>
            Орлогын загвар: {revenueModelLabel[model] ?? model} · {contract.currency}
          </Text>
          <Tooltip
            label={pdfAvailable ? "Гэрээний PDF" : "PDF татах боломж удахгүй нэмэгдэнэ"}
            withArrow
          >
            <Button
              mt="sm"
              variant="white"
              color="brand"
              size="sm"
              leftSection={<IconFileDownload size={18} />}
              disabled={!pdfAvailable}
              loading={pdfLoading}
              onClick={onViewPdf}
            >
              Гэрээний PDF харах
            </Button>
          </Tooltip>
        </Box>
      </Group>
    </Card>
  );
}
